import { useMemo } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import type { SelectChangeEvent } from '@mui/material';
import {
  Autocomplete,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@/primitives';
import { getOperatorsByField } from '../helpers';
import type { DynamicFieldOption } from '../constants';

export type DynamicFilterRowData = {
  id: string;
  field: string;
  operator: string;
  value: string;
};

interface Props {
  row: DynamicFilterRowData;
  fieldOptions: DynamicFieldOption[];
  onUpdate: (
    id: string,
    patch: Partial<Omit<DynamicFilterRowData, 'id'>>,
  ) => void;
  onDelete: (id: string) => void;
  showDelete: boolean;
}

const inputSx = {
  fieldset: {
    border: `1px solid`,
    borderColor: 'grey.500',
    borderRadius: '8px',
  },
};

export default function DynamicFilterRow({
  row,
  fieldOptions,
  onUpdate,
  onDelete,
  showDelete,
}: Props) {
  const selectedFieldOption = useMemo(
    () => fieldOptions.find((o) => o.value === row.field) ?? null,
    [fieldOptions, row.field],
  );

  const operators = useMemo(
    () => (row.field ? getOperatorsByField(row.field) : []),
    [row.field],
  );

  function handleFieldChange(
    _event: React.SyntheticEvent,
    newValue: DynamicFieldOption | string | null,
  ) {
    const option = typeof newValue === 'string' ? null : newValue;
    onUpdate(row.id, {
      field: option?.value ?? '',
      operator: '',
      value: '',
    });
  }

  function handleOperatorChange(nextOperator: string) {
    onUpdate(row.id, { operator: nextOperator });
  }

  function handleValueChange(nextValue: string) {
    onUpdate(row.id, { value: nextValue });
  }

  return (
    <Stack
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: '12px', md: '12px' },
        mb: '12px',
        alignItems: { md: 'flex-start' },
      }}
    >
      <Autocomplete<DynamicFieldOption, false, false, false>
        options={fieldOptions}
        groupBy={(option) => option.category ?? ''}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.label
        }
        isOptionEqualToValue={(a, b) => a.value === b.value}
        value={selectedFieldOption}
        onChange={handleFieldChange}
        size="small"
        sx={{ flex: 1, minWidth: 0 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Field Detail"
            placeholder="Select field"
            slotProps={{
              ...params.slotProps,
              inputLabel: { ...params.slotProps.inputLabel, shrink: true },
            }}
            sx={{
              ...inputSx,
              '& .MuiInputBase-root': { height: '56px', borderRadius: '8px' },
            }}
          />
        )}
      />

      <FormControl
        sx={{ flex: 0.7, minWidth: { xs: '100%', md: '160px' } }}
        size="small"
      >
        <InputLabel shrink id={`dyn-op-${row.id}`}>
          Field Operator
        </InputLabel>
        <Select
          displayEmpty
          label="Field Operator"
          labelId={`dyn-op-${row.id}`}
          notched
          value={row.operator}
          disabled={!row.field}
          onChange={(e: SelectChangeEvent<unknown>) =>
            handleOperatorChange(e.target.value as string)
          }
          renderValue={(val) => {
            if (!val) {
              return (
                <Typography
                  variant="titlemedium"
                  sx={{ color: 'inherit', opacity: 0.5 }}
                >
                  Select
                </Typography>
              );
            }
            const found = operators.find((op) => op.value === val);
            return (
              <Typography variant="titlemedium">
                {found?.label ?? (val as string)}
              </Typography>
            );
          }}
          sx={{
            height: '56px',
            '.MuiSelect-select': { px: '12px' },
            ...inputSx,
            svg: { right: '10px', color: 'text.primary' },
          }}
        >
          <MenuItem sx={{ height: '36px' }} value="" />
          {operators.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              <Typography variant="titlemedium">{label}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Value"
        placeholder="Enter value"
        size="small"
        value={row.value}
        disabled={!row.field || !row.operator}
        onChange={(e) => handleValueChange(e.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{
          flex: 1,
          minWidth: 0,
          ...inputSx,
          '& .MuiInputBase-root': { height: '56px', borderRadius: '8px' },
          input: {
            px: '12px',
            fontSize: '16px',
            fontWeight: 500,
          },
        }}
      />

      {showDelete ? (
        <IconButton
          aria-label="Remove filter"
          onClick={() => onDelete(row.id)}
          sx={{
            alignSelf: { xs: 'flex-end', md: 'center' },
            color: 'text.secondary',
            mt: { md: '4px' },
          }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      ) : (
        <Stack sx={{ width: '40px', flexShrink: 0 }} />
      )}
    </Stack>
  );
}
